import { IMPERSONATION_TOKEN_KEY } from '@klareospace/pharos-lib-front-utils/auth';

// --- Constants ---

const IMPERSONATION_TOKEN_SOURCE = {
  LOCAL_STORAGE: 'localStorage',
  URL: 'url',
} as const;
type ImpersonationTokenSource = (typeof IMPERSONATION_TOKEN_SOURCE)[keyof typeof IMPERSONATION_TOKEN_SOURCE];

/**
 * Resolves the impersonation token: URL param first, then `localStorage`, else `null`.
 * A URL token is persisted to `localStorage` and stripped from the address bar.
 * Must run synchronously before the first render (used as a lazy `useState` initializer).
 */
export function resolveImpersonationToken(): string | null {
  const params = new URLSearchParams(window.location.search);
  const urlToken = params.get(IMPERSONATION_TOKEN_KEY);

  if (urlToken) {
    stripTokenFromUrl();
    persistToken(urlToken);
    logTokenSource(IMPERSONATION_TOKEN_SOURCE.URL);
    return urlToken;
  }

  const storedToken = readPersistedToken();
  if (storedToken) logTokenSource(IMPERSONATION_TOKEN_SOURCE.LOCAL_STORAGE);
  return storedToken;
}

// --- Helpers ---

function stripTokenFromUrl(): void {
  const url = new URL(window.location.href);
  url.searchParams.delete(IMPERSONATION_TOKEN_KEY);
  window.history.replaceState({}, '', url.toString());
}

/** Persists the token to localStorage; never throws when storage is blocked. */
function persistToken(token: string): void {
  try {
    localStorage.setItem(IMPERSONATION_TOKEN_KEY, token);
  } catch (error: unknown) {
    console.warn('[ImpersonationToken] Failed to persist impersonation token to localStorage:', error);
  }
}

/** Reads the token from localStorage; returns null when storage is blocked instead of throwing. */
function readPersistedToken(): string | null {
  try {
    return localStorage.getItem(IMPERSONATION_TOKEN_KEY);
  } catch (error: unknown) {
    console.warn('[ImpersonationToken] Failed to read impersonation token from localStorage:', error);
    return null;
  }
}

function logTokenSource(source: ImpersonationTokenSource): void {
  if (import.meta.env.DEV) console.debug('[ImpersonationToken] Impersonation token resolved', { source });
}
